import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLeadPicker, useLinkEnquiryToLead } from "../hooks/useLeads";
import { LeadStatusBadge } from "./LeadStatusBadge";

/**
 * "Link to existing Lead…" picker used from Enquiry detail. Pre-fills the
 * search box with the enquiry's email so the most-likely candidates are
 * always one keystroke away. The picker only shows non-merged, non-deleted
 * Leads — manual link won't reattach to a merged-away record.
 */
export function LinkLeadModal({
  enquiryId,
  enquiryEmail,
  enquiryPhone,
  enquiryName,
  open,
  onOpenChange,
}: {
  enquiryId: string;
  enquiryEmail: string | null;
  enquiryPhone: string | null;
  enquiryName: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const initial = enquiryEmail || enquiryPhone || enquiryName || "";
  const [search, setSearch] = useState(initial);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const candidates = useLeadPicker(search, open);
  const link = useLinkEnquiryToLead(enquiryId);

  async function attach(leadId: string) {
    setPendingId(leadId);
    try {
      await link.mutateAsync(leadId);
      onOpenChange(false);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Link to existing Lead</DialogTitle>
          <DialogDescription>
            Search by name, email, phone, company, or LEA code. Pick the Lead
            this enquiry belongs to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              autoFocus
              placeholder="Search Leads…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div
            className="max-h-[320px] overflow-y-auto rounded-md border"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {candidates.isLoading ? (
              <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Searching…
              </div>
            ) : search.trim().length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Type to search.
              </div>
            ) : (candidates.data ?? []).length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No Leads match.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {(candidates.data ?? []).map((l) => (
                  <li
                    key={l.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">
                          {l.code}
                        </span>
                        <span className="truncate text-sm font-medium text-foreground">
                          {l.name ?? "(no name)"}
                        </span>
                        <LeadStatusBadge status={l.status} />
                      </div>
                      <div className="mt-1 truncate text-xs text-muted-foreground">
                        {l.email ?? "—"}
                        {l.phone ? ` · ${l.phone}` : ""}
                        {l.companyName ? ` · ${l.companyName}` : ""}
                        {" · "}
                        {l.enquiryCount} enquir{l.enquiryCount === 1 ? "y" : "ies"}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => attach(l.id)}
                      disabled={pendingId !== null}
                    >
                      {pendingId === l.id && (
                        <Loader2
                          className="mr-1 h-3 w-3 animate-spin"
                          strokeWidth={1.5}
                        />
                      )}
                      Link
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
